import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import PortalLayout from "../components/PortalLayout";
import DocumentPreview from "../components/DocumentPreview";
import {
  Upload,
  Plus,
  Trash2,
  FileText,
  FileUp,
  Loader2,
  Eye,
  ShieldCheck,
} from "lucide-react";

const FY_OPTIONS = [];
for (let y = 2021; y <= new Date().getFullYear() + 2; y++) {
  FY_OPTIONS.push(`${y}-${String(y + 1).slice(-2)}`);
}

const GST_RATE_OPTIONS = [
  { value: "non_gst", label: "Non-GST / Unregistered" },
  { value: "zero_rated", label: "Zero Rated / Exempted" },
  { value: "1", label: "1%" },
  { value: "3", label: "3%" },
  { value: "5", label: "5%" },
  { value: "12", label: "12%" },
  { value: "18", label: "18%" },
  { value: "28", label: "28%" },
];

const EMPTY_FORM = {
  date: "",
  invoice_no: "",
  payee: "",
  vendor_city: "",
  vendor_state: "",
  vendor_pincode: "",
  vendor_gst_no: "",
  hsn_code: "",
  quantity: "",
  category: "",
  total_taxable_value: "",
  discount: "",
  amount: "",
  gst_rate: "",
  description: "",
  file_url: "",
};

const AccountingView = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [autoCategorizing, setAutoCategorizing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("transactions");
  const [selectedFY, setSelectedFY] = useState("");
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [preview, setPreview] = useState(null);
  const vendorRef = useRef(null);

  const [bankUploadForm, setBankUploadForm] = useState({ file: null, notes: "", profile_id: "" });

  // Fetch profiles
  useEffect(() => {
    authFetch(`${API}/profiles`).then(r => r.ok ? r.json() : []).then(setProfiles).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const fyParam = selectedFY && selectedFY !== "all" ? `?fy=${selectedFY}` : "";
      const [txnResponse, docResponse] = await Promise.all([
        authFetch(`${API}/transactions${fyParam}`),
        authFetch(`${API}/documents/my-documents`),
      ]);
      if (txnResponse.ok) setTransactions(await txnResponse.json());
      if (docResponse.ok) {
        const docs = await docResponse.json();
        setDocuments(docs.filter((d) => d.service_type === "accounting"));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedFY]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchVendorSuggestions = async (q) => {
    if (q.length < 2) { setVendorSuggestions([]); return; }
    try {
      const res = await authFetch(`${API}/vendors/search?q=${encodeURIComponent(q)}`);
      if (res.ok) setVendorSuggestions(await res.json());
    } catch { /* ignore */ }
  };

  // Standard file upload for invoice attachment
  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_category", "invoice");
    formData.append("service_type", "accounting");
    try {
      const res = await authFetch(`${API}/documents/upload`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setForm((prev) => ({ ...prev, file_url: data.document?.file_url || "" }));
        toast.success("Invoice uploaded. Fill in the transaction details below.");
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    if (!form.payee || !form.category || !form.amount || !form.date) {
      toast.error("Please fill all required fields");
      return;
    }
    if (profiles.length > 0 && !selectedProfileId) {
      toast.error("Please select a profile to tag this transaction");
      return;
    }
    if (form.gst_rate && form.gst_rate !== "non_gst" && form.gst_rate !== "zero_rated" && !form.vendor_gst_no) {
      toast.error("Vendor GST No. is required when a GST rate is selected");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        quantity: form.quantity ? parseFloat(form.quantity) : null,
        total_taxable_value: form.total_taxable_value ? parseFloat(form.total_taxable_value) : null,
        discount: form.discount ? parseFloat(form.discount) : null,
        gst_rate: form.gst_rate || null,
        file_url: form.file_url || null,
        profile_id: selectedProfileId || null,
      };

      const response = await authFetch(`${API}/transactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success("Transaction saved");
        setForm({ ...EMPTY_FORM });
        setSelectedProfileId("");
        fetchData();
      } else {
        toast.error("Failed to save transaction");
      }
    } catch {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      const res = await authFetch(`${API}/transactions/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Deleted"); fetchData(); }
    } catch { toast.error("Failed to delete"); }
  };

  const handleAutoCategorize = async () => {
    if (transactions.length === 0) return;
    setAutoCategorizing(true);
    try {
      const ids = transactions.map(t => t.transaction_id);
      const res = await authFetch(`${API}/transactions/auto-categorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transaction_ids: ids }),
      });
      if (!res.ok) throw new Error("Auto categorization failed");
      toast.success("Categorization started! Refresh in a few seconds.");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAutoCategorizing(false);
    }
  };

  const handleBankUpload = async (e) => {
    e.preventDefault();
    if (!bankUploadForm.file) { toast.error("Select a file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", bankUploadForm.file);
    fd.append("document_category", "bank_statement");
    fd.append("service_type", "accounting");
    fd.append("notes", bankUploadForm.notes);
    if (bankUploadForm.profile_id) fd.append("profile_id", bankUploadForm.profile_id);
    try {
      const res = await authFetch(`${API}/documents/upload`, { method: "POST", body: fd });
      if (res.ok) {
        toast.success("Bank statement uploaded");
        setBankUploadForm({ file: null, notes: "", profile_id: "" });
        fetchData();
      } else { toast.error("Upload failed"); }
    } catch { toast.error("An error occurred"); }
    finally { setUploading(false); }
  };

  const gstLabel = (rate) => {
    const opt = GST_RATE_OPTIONS.find((o) => o.value === rate);
    return opt ? opt.label : rate || "-";
  };

  const getProfileName = (profileId) => {
    if (!profileId) return null;
    const p = profiles.find((pr) => pr.profile_id === profileId);
    return p?.full_name || null;
  };

  const getDocMeta = (fileUrl) => {
    if (!fileUrl) return null;
    const docId = fileUrl.split("/").pop();
    return documents.find(d => d.file_id === docId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <PortalLayout title="Accounting">
      <div className="relative z-10 w-full pb-4">
        {/* Security Banner */}
        <div className="flex items-center gap-2 mb-3 p-4 rounded-[20px] bg-white border border-[var(--border-light)] shadow-sm" data-testid="security-badge-accounting">
          <ShieldCheck className="w-5 h-5 text-[var(--success)] shrink-0" />
          <span className="text-sm font-medium text-[var(--text-muted)]">
            <strong className="text-[var(--text-main)] text-sm">Secured Vault:</strong> AES-256 Encrypted Storage &middot; Cloudflare-Powered &middot; Your financial data is private &amp; secured
          </span>
        </div>
        {/* Tab bar + FY filter */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-3 soft-card !p-3">
          <div className="flex gap-2 p-1.5 rounded-full bg-[var(--background-primary)] border border-[var(--border-light)]">
            {["transactions", "bank-statements"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 text-sm font-bold rounded-full transition-all duration-300 ${
                  activeTab === tab ? "bg-[var(--primary)] text-white shadow-md" : "text-[var(--text-muted)] hover:bg-[var(--primary)]/5"
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === "transactions" ? "Transactions" : "Bank Statements"}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 px-3">
            <span className="text-sm font-bold text-[var(--text-muted)]">Financial Year:</span>
            <Select value={selectedFY} onValueChange={setSelectedFY}>
              <SelectTrigger className="w-[140px] rounded-xl border-[var(--border-light)] font-medium" data-testid="fy-filter">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {FY_OPTIONS.map((fy) => (
                  <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {activeTab === "transactions" && (
          <div className="grid lg:grid-cols-5 gap-4">
            {/* Left: Transaction Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="soft-card self-start h-fit"
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-['Playfair_Display'] text-xl font-bold text-[var(--text-main)]">
                    Add Transaction
                  </h2>
                </div>

                {/* File Upload */}
                <div
                  className="mb-5 p-6 rounded-[20px] border-2 border-dashed border-[var(--border-light)] bg-[var(--background-primary)] text-center cursor-pointer hover:border-[var(--primary)] transition-all duration-300"
                  data-testid="file-upload-area"
                >
                  <input
                    type="file"
                    id="invoice-upload"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleInvoiceUpload}
                    disabled={uploading}
                  />
                  <label htmlFor="invoice-upload" className="cursor-pointer flex flex-col items-center">
                    <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] mb-4 group-hover:scale-105 transition-transform">
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-[var(--primary)] animate-spin" />
                      ) : (
                        <FileUp className="w-6 h-6 text-[var(--primary)]" />
                      )}
                    </div>
                    <span className="text-sm font-bold text-[var(--text-main)]">
                      {uploading ? "Uploading..." : "Attach Invoice"}
                    </span>
                    <span className="text-xs font-medium mt-1 text-[var(--text-muted)]">
                      PDF, JPG, or PNG
                    </span>
                  </label>
                </div>

                {/* Preview the uploaded file if available */}
                {form.file_url && (
                  <button
                    type="button"
                    onClick={() => {
                      const docMeta = getDocMeta(form.file_url);
                      setPreview({ 
                        fileUrl: form.file_url, 
                        filename: docMeta?.filename || "Uploaded Invoice", 
                        contentType: docMeta?.content_type || "" 
                      });
                    }}
                    className="w-full mb-3 flex items-center justify-center gap-2 p-3 rounded-[20px] border border-[var(--border-light)] text-sm font-bold text-[var(--primary)] hover:bg-[var(--primary)]/5 transition-all duration-300 shadow-sm bg-white"
                    data-testid="preview-uploaded-invoice"
                  >
                    <Eye className="w-4 h-4" />
                    Preview Uploaded Invoice
                  </button>
                )}

                <form onSubmit={handleTransactionSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Date *</label>
                      <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border-[var(--border-light)]" data-testid="txn-date" />
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Invoice No.</label>
                      <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} placeholder="INV-001" className="rounded-xl border-[var(--border-light)]" data-testid="txn-invoice-no" />
                    </div>
                  </div>

                  {/* Vendor Name with autocomplete */}
                  <div className="relative" ref={vendorRef}>
                    <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Vendor Name *</label>
                    <Input
                      value={form.payee}
                      onChange={(e) => {
                        setForm({ ...form, payee: e.target.value });
                        fetchVendorSuggestions(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => form.payee.length >= 2 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Start typing..."
                      className="rounded-xl border-[var(--border-light)]"
                      data-testid="txn-payee"
                    />
                    {showSuggestions && vendorSuggestions.length > 0 && (
                      <div
                        className="absolute z-20 w-full mt-2 rounded-[20px] shadow-lg max-h-48 overflow-y-auto border border-[var(--border-light)] bg-white p-2"
                      >
                        {vendorSuggestions.map((s, i) => (
                          <button
                            key={i}
                            type="button"
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-[var(--primary)]/5 transition-colors cursor-pointer rounded-xl mb-1 last:mb-0"
                            onMouseDown={() => {
                              setForm({
                                ...form,
                                payee: s.payee || s,
                                vendor_city: s.vendor_city || form.vendor_city,
                                vendor_state: s.vendor_state || form.vendor_state,
                                vendor_pincode: s.vendor_pincode || form.vendor_pincode,
                                vendor_gst_no: s.vendor_gst_no || form.vendor_gst_no,
                              });
                              setShowSuggestions(false);
                            }}
                          >
                            <span className="font-bold text-[var(--text-main)]">{s.payee || s}</span>
                            {(s.vendor_city || s.vendor_state) && (
                              <span className="text-xs ml-2 font-medium text-[var(--text-muted)]">
                                — {[s.vendor_city, s.vendor_state].filter(Boolean).join(", ")}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">City</label>
                      <Input value={form.vendor_city} onChange={(e) => setForm({ ...form, vendor_city: e.target.value })} placeholder="City" className="rounded-xl border-[var(--border-light)]" data-testid="txn-city" />
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">State</label>
                      <Input value={form.vendor_state} onChange={(e) => setForm({ ...form, vendor_state: e.target.value })} placeholder="State" className="rounded-xl border-[var(--border-light)]" data-testid="txn-state" />
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Pincode</label>
                      <Input value={form.vendor_pincode} onChange={(e) => setForm({ ...form, vendor_pincode: e.target.value })} placeholder="000000" className="rounded-xl border-[var(--border-light)]" data-testid="txn-pincode" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">GST Rate</label>
                      <Select value={form.gst_rate} onValueChange={(v) => setForm({ ...form, gst_rate: v })}>
                        <SelectTrigger className="rounded-xl border-[var(--border-light)] hover:border-[var(--primary)] transition-all cursor-pointer" data-testid="txn-gst-rate">
                          <SelectValue placeholder="Select rate" />
                        </SelectTrigger>
                        <SelectContent>
                          {GST_RATE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">
                        Vendor GST No.{form.gst_rate && form.gst_rate !== "non_gst" && form.gst_rate !== "zero_rated" ? " *" : ""}
                      </label>
                      <Input
                        value={form.vendor_gst_no}
                        onChange={(e) => setForm({ ...form, vendor_gst_no: e.target.value.toUpperCase() })}
                        placeholder="22AAAAA0000A1Z5"
                        className="rounded-xl border-[var(--border-light)]"
                        data-testid="txn-gst-no"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">HSN Code</label>
                      <Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} placeholder="HSN/SAC" className="rounded-xl border-[var(--border-light)]" data-testid="txn-hsn" />
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Category *</label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger className="rounded-xl border-[var(--border-light)] hover:border-[var(--primary)] transition-all cursor-pointer" data-testid="txn-category">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="purchases">Purchases</SelectItem>
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="expenses">Expenses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Quantity</label>
                      <Input type="number" step="any" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="0" className="rounded-xl border-[var(--border-light)]" data-testid="txn-qty" />
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Taxable Value</label>
                      <Input type="number" step="any" value={form.total_taxable_value} onChange={(e) => setForm({ ...form, total_taxable_value: e.target.value })} placeholder="0.00" className="rounded-xl border-[var(--border-light)]" data-testid="txn-taxable" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Discount</label>
                      <Input type="number" step="any" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} placeholder="0.00" className="rounded-xl border-[var(--border-light)]" data-testid="txn-discount" />
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Total Amount (incl. tax) *</label>
                      <Input type="number" step="any" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="rounded-xl border-[var(--border-light)]" data-testid="txn-amount" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Description</label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Optional notes..."
                      rows={2}
                      className="rounded-xl border-[var(--border-light)]"
                      data-testid="txn-desc"
                    />
                  </div>

                  {profiles.length > 0 && (
                    <div>
                      <label className="text-xs font-bold mb-1.5 block text-[var(--text-muted)]">Tag Profile *</label>
                      <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                        <SelectTrigger className="rounded-xl border-[var(--border-light)]" data-testid="txn-profile-select">
                          <SelectValue placeholder="Select profile" />
                        </SelectTrigger>
                        <SelectContent>
                          {profiles.map((p) => (
                            <SelectItem key={p.profile_id} value={p.profile_id}>{p.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="soft-btn w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-transaction-btn"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
                    {submitting ? "Saving..." : "Save Transaction"}
                  </button>
                </form>
              </motion.div>
            </div>

            {/* Right: Transactions list */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="soft-card h-full"
                data-testid="transactions-list"
              >
                <div className="flex justify-between items-center mb-3">
                  <h2 className="font-['Playfair_Display'] text-xl font-bold text-[var(--text-main)]">
                    Transactions {selectedFY && selectedFY !== "all" ? `(FY ${selectedFY})` : ""}
                  </h2>
                  {transactions.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleAutoCategorize}
                      disabled={autoCategorizing}
                      className="border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)]/10"
                    >
                      {autoCategorizing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : "🤖 "} Auto-Categorize
                    </Button>
                  )}
                </div>

                {transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>GST</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((txn) => (
                          <TableRow key={txn.transaction_id}>
                            <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                              {txn.date ? new Date(txn.date).toLocaleDateString("en-IN") : "-"}
                            </TableCell>
                            <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                              {txn.invoice_no || "-"}
                            </TableCell>
                            <TableCell className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
                              {txn.payee}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                ["sales", "consulting income", "other income"].includes(txn.category?.toLowerCase()) ? "bg-[#10B981]/15 text-[#10B981]"
                                : ["purchases", "rent", "office supplies", "travel"].includes(txn.category?.toLowerCase()) ? "bg-[#F59E0B]/15 text-[#F59E0B]"
                                : "bg-[#EF4444]/15 text-[#EF4444]"
                              }`}>
                                {txn.category?.charAt(0).toUpperCase() + txn.category?.slice(1)}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                              {gstLabel(txn.gst_rate)}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium" style={{ color: "var(--text-primary)" }}>
                              {"\u20B9"}{txn.amount?.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {txn.file_url && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const docMeta = getDocMeta(txn.file_url);
                                      setPreview({ 
                                        fileUrl: txn.file_url, 
                                        filename: docMeta?.filename || `Invoice ${txn.invoice_no || ""}`, 
                                        contentType: docMeta?.content_type || "" 
                                      });
                                    }}
                                    className="text-[var(--accent)] hover:bg-[var(--accent-bg)] transition-all duration-300"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteTransaction(txn.transaction_id)}
                                  className="text-[#EF4444] hover:bg-[#EF4444]/10 transition-all duration-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-16 px-4 bg-[var(--background-primary)] border border-dashed border-[var(--border-light)] rounded-[20px]">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-sm border border-[var(--border-light)] flex items-center justify-center">
                      <FileText className="w-10 h-10 text-[var(--text-muted)]/50" />
                    </div>
                    <p className="font-bold text-lg mb-2 text-[var(--text-main)]">No transactions recorded yet</p>
                    <p className="text-sm max-w-sm mx-auto text-[var(--text-muted)]">
                      Start recording your income and expense transactions using the form above to keep your books organized.
                    </p>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        )}

        {/* Bank Statements Tab */}
        {activeTab === "bank-statements" && (
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="soft-card self-start h-fit">
                <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3 text-[var(--text-main)]">
                  Upload Bank Statement
                </h2>
                <form onSubmit={handleBankUpload} className="space-y-4">
                  <div
                    className="p-6 rounded-[20px] border-2 border-dashed border-[var(--border-light)] bg-[var(--background-primary)] text-center cursor-pointer hover:border-[var(--primary)] transition-all duration-300"
                    data-testid="bank-statement-upload-area"
                  >
                    <input
                      type="file"
                      id="bank-upload"
                      className="hidden"
                      accept=".pdf,.xls,.xlsx,.csv"
                      onChange={(e) => setBankUploadForm({ ...bankUploadForm, file: e.target.files[0] })}
                      data-testid="bank-statement-input"
                    />
                    <label htmlFor="bank-upload" className="cursor-pointer flex flex-col items-center">
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] mb-4 group-hover:scale-105 transition-transform">
                        <Upload className="w-6 h-6 text-[var(--primary)]" />
                      </div>
                      {bankUploadForm.file ? (
                        <span className="text-sm font-bold text-[var(--text-main)]">{bankUploadForm.file.name}</span>
                      ) : (
                        <>
                          <span className="text-sm font-bold text-[var(--text-main)]">Monthly bank statement</span>
                          <span className="text-xs font-medium mt-1 text-[var(--text-muted)]">PDF, XLS, CSV</span>
                        </>
                      )}
                    </label>
                  </div>
                  <Input
                    value={bankUploadForm.notes}
                    onChange={(e) => setBankUploadForm({ ...bankUploadForm, notes: e.target.value })}
                    placeholder="Month/Year (e.g., January 2026)"
                    className="rounded-xl border-[var(--border-light)]"
                  />
                  {profiles.length > 0 && (
                    <Select value={bankUploadForm.profile_id} onValueChange={(v) => setBankUploadForm({ ...bankUploadForm, profile_id: v })}>
                      <SelectTrigger data-testid="bank-profile-select" className="rounded-xl border-[var(--border-light)]">
                        <SelectValue placeholder="Tag a Profile *" />
                      </SelectTrigger>
                      <SelectContent>
                        {profiles.map((p) => (
                          <SelectItem key={p.profile_id} value={p.profile_id}>{p.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <button
                    type="submit"
                    disabled={uploading || !bankUploadForm.file}
                    className="soft-btn w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="upload-bank-btn"
                  >
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Upload className="w-5 h-5 mr-2" />}
                    {uploading ? "Uploading..." : "Upload Statement"}
                  </button>
                </form>
              </motion.div>
            </div>

            <div className="lg:col-span-3">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="soft-card h-full" data-testid="bank-statements-list">
                <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3 text-[var(--text-main)]">
                  Uploaded Bank Statements
                </h2>
                {documents.filter((d) => d.document_category === "bank_statement").length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Filename</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Upload Date</TableHead>
                        <TableHead className="text-right">Preview</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents
                        .filter((d) => d.document_category === "bank_statement")
                        .map((doc) => (
                          <TableRow key={doc.document_id}>
                            <TableCell className="font-medium text-sm" style={{ color: "var(--text-primary)" }}>
                              {doc.filename}
                            </TableCell>
                            <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                              {doc.notes || "-"}
                            </TableCell>
                            <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                              {new Date(doc.uploaded_at).toLocaleDateString("en-IN")}
                            </TableCell>
                            <TableCell className="text-right">
                              {doc.file_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPreview({ fileUrl: doc.file_url, filename: doc.filename, contentType: doc.content_type })}
                                  className="text-[var(--accent)] hover:bg-[var(--accent-bg)]"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-16 px-4 bg-[var(--background-primary)] border border-dashed border-[var(--border-light)] rounded-[20px]">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-sm border border-[var(--border-light)] flex items-center justify-center">
                      <Upload className="w-10 h-10 text-[var(--text-muted)]/50" />
                    </div>
                    <p className="font-bold text-lg mb-2 text-[var(--text-main)]">No bank statements uploaded yet</p>
                    <p className="text-sm max-w-sm mx-auto text-[var(--text-muted)]">
                      Upload your bank statements to help your CA team track and reconcile transactions.
                    </p>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {preview && (
        <DocumentPreview document={preview} onClose={() => setPreview(null)} />
      )}
    
    </PortalLayout>
  );
};

export default AccountingView;
